import { StrictMode } from 'react'
import type { ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import type { ErrorEvent, EventHint } from '@sentry/react'
import '@fontsource/outfit/latin-300.css'
import '@fontsource/outfit/latin-400.css'
import '@fontsource/outfit/latin-500.css'
import '@fontsource/outfit/latin-600.css'
import '@fontsource/outfit/latin-700.css'
import '@fontsource/geist-mono/latin-400.css'
import '@fontsource/geist-mono/latin-500.css'
import '@fontsource/geist-mono/latin-600.css'
import './index.css'
import { initConfig, getSentryDsn, getSentryEnvironment } from './config'
import { initReplay, startReplayBuffer } from './lib/replay'
import { isTransientBrowserError, shouldCreateSpanForRequest } from './lib/observability/tracingFilters'
import { probeStorage } from './lib/storage/probeStorage'
import { registerSW } from './lib/swRegistration'
import { attemptBootRecovery, showBootFailure } from './lib/bootRecovery'
import { keepBoardCountsFromDedupe } from './modules/shared/board-data/engine/boardFallback'
import { getRunOfShowServerRenderRequest } from './modules/events/components/ros/runOfShowServerRender'
import type { RunOfShowServerRenderRequest } from './modules/events/components/ros/runOfShowServerRender'

import { getReportServerRenderRequest } from './modules/reports/print/reportServerRender'
import type { ReportServerRenderRequest } from './modules/reports/print/reportServerRender'

const runOfShowServerRenderRequest = getRunOfShowServerRenderRequest(
  new URL(window.location.href),
)
const reportServerRenderRequest = getReportServerRenderRequest(
  new URL(window.location.href),
)
if (runOfShowServerRenderRequest || reportServerRenderRequest) {
  // Keep the short-lived credential out of browser history, diagnostics, and
  // any later error report. The in-memory request object remains the only copy.
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${window.location.search}`,
  )
}

/**
 * Filter transient browser errors and enrich Sentry events.
 *
 * AbortError: Normal browser behavior when fetch requests are cancelled
 * (user navigation, component unmount, React Query cleanup). Not a bug.
 *
 * NetworkError (DOMException code 19): Browser-level network failure thrown
 * by Safari when a fetch fails at the network layer (e.g. service worker
 * returning a failed response, transient connectivity loss). Axios requests
 * are already classified as transient by isTransientApiError; raw DOMExceptions
 * from other fetch paths (SW fallback, Sentry tunnel) reach beforeSend
 * unfiltered. Same non-actionable category as AbortError. (GlitchTip #3433)
 *
 * When a non-Error value is thrown (undefined, null, plain object) or an
 * Error is constructed with no message, GlitchTip displays "No error
 * message" which makes triage impossible. This hook fills in the blank.
 */
function enrichSentryEvent(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
  const exc = event.exception?.values?.[0];
  const original = hint.originalException;

  // AbortError and NetworkError are browser-level, not application bugs. The same
  // check runs in the service worker's Sentry init (tracingFilters.ts).
  if (isTransientBrowserError(exc, original)) {
    return null;
  }

  // Filter out Sentry instrumentation errors on Bryntum elements
  // Sentry's browser tracing tries to call .closest().dataset without null-checking
  // on Bryntum terminal elements (used for drag/resize), causing false positive errors
  if (
    exc?.type === 'TypeError' &&
    exc?.value?.includes("null is not an object (evaluating") &&
    exc?.value?.includes(".dataset")
  ) {
    return null;
  }

  // Enrich empty exception messages
  if (exc && !exc.value) {
    if (original === undefined || original === null) {
      exc.value = `Non-Error rejection captured: ${String(original)}`;
    } else if (typeof original === 'object' && !(original instanceof Error)) {
      try {
        exc.value = `Non-Error object thrown: ${JSON.stringify(original).slice(0, 200)}`;
      } catch {
        // EXPECTED_DOMAIN_ERROR: we're already inside Sentry enrichment, so
        // routing this through reportError would recurse back into beforeSend.
        exc.value = `Non-Error object thrown: ${Object.prototype.toString.call(original)}`;
      }
    } else if (original instanceof Error) {
      exc.value = original.stack?.split('\n')[0] || original.constructor.name || 'Unknown Error';
    } else {
      exc.value = `Non-Error value thrown: ${String(original).slice(0, 200)}`;
    }
  }
  return event;
}

type TestSentryWindow = Window & {
  __PLAYWRIGHT__?: boolean
  __SENTRY__?: typeof Sentry
}

function renderStrict(node: ReactNode) {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      {node}
    </StrictMode>,
  )
}

async function bootServerPrintRender(
  request: RunOfShowServerRenderRequest,
): Promise<void> {
  // Headless PDF render path — Gotenberg/Chromium visiting the `?print=1` route.
  //
  // Mount ONLY the signed print document, deliberately BEFORE (and instead of)
  // initConfig / Sentry.init / initReplay / startReplayBuffer. The renderer runs
  // under a locked-egress NetworkPolicy (DNS plus the in-cluster frontend only),
  // so every one of those boot steps reaches for an origin it cannot talk to —
  // the runtime-config fetch, the Sentry tunnel, and the replay upload channel —
  // and stalls the render for seconds before each times out. That fixed cold
  // boot, not the layout, is the ~8–27s the print render was paying.
  //
  // The print document needs none of it: a static theme, the query client, and
  // one same-origin projection fetch through the frontend proxy. Skipping the
  // observability/config prelude renders the EXACT same DOM (same components,
  // same client runtime, same fonts+layout settle) as the interactive path,
  // just without the boot that can only fail here.
  try {
    const { default: RunOfShowServerPrintApp } = await import(
      './modules/events/components/ros/RunOfShowServerPrintApp'
    )
    renderStrict(<RunOfShowServerPrintApp {...request} />)
  } catch (err) {
    // Sentry is intentionally not initialized on this path, so captureException
    // is a safe no-op; attemptBootRecovery still handles a stale-chunk reload.
    await attemptBootRecovery(
      err,
      () => window.location.reload(),
      (e) => Sentry.captureException(e, { extra: { where: 'main.bootServerPrintRender' } }),
    )
  }
}

// Load runtime config before rendering the interactive shells.
function bootApp(): Promise<void> {
  return initConfig().then(async () => {
    const dsn = getSentryDsn();
    if (dsn) {
      Sentry.init({
        dsn,
        environment: getSentryEnvironment() || 'development',
        release: __FRONTEND_BUILD_ID__,
        tunnel: '/sentry-tunnel',
        integrations: (defaults) => [
          ...keepBoardCountsFromDedupe(defaults),
          // shouldCreateSpanForRequest excludes the useOnlineStatus health
          // probe, whose deliberate offline failures would otherwise be traced
          // as "no-response" spans (GlitchTip #3064). Real requests stay traced.
          Sentry.browserTracingIntegration({ shouldCreateSpanForRequest }),
          initReplay(),
        ],
        tracesSampleRate: 0.1,
        sendDefaultPii: false,
        beforeSend: enrichSentryEvent,
      });
      // Always-on rolling replay buffer: records locally from boot, uploads
      // nothing until the user attaches it to a bug report (flushReplay).
      startReplayBuffer();
    }

    const testWindow = window as TestSentryWindow
    if (import.meta.env.MODE === 'test' || testWindow.__PLAYWRIGHT__) {
      testWindow.__SENTRY__ = Sentry
    }

    probeStorage()

    if (window.location.pathname.startsWith('/client')) {
      const { default: ClientPortalApp } = await import('./modules/client-portal/ClientPortalApp')
      renderStrict(<ClientPortalApp />)
      registerSW()
      return
    }

    const { default: App } = await import('./App.tsx')
    renderStrict(<App />)

    // /sw.js owns app-shell updates, push notifications, guest-portal caching,
    // and the offline shell. OFFLINE_PWA_ENABLED only gates staff offline
    // sync/data UX inside React and apiClient.
    registerSW()
  }).catch(async (err) => {
    // A dynamic import can fail when the service worker serves a stale chunk
    // (e.g. old precache + new index.html after a deploy) or on a transient
    // mobile network failure.  attemptBootRecovery signals SKIP_WAITING to the
    // pending SW before reloading so the new SW's precache is active on the
    // reloaded page; the sessionStorage flag prevents an infinite reload loop.
    // Persistent failures or non-import errors are forwarded to Sentry.
    await attemptBootRecovery(
      err,
      () => window.location.reload(),
      (e) => {
        Sentry.captureException(e, { extra: { where: 'main.boot' } })
        showBootFailure(document.getElementById('root'), navigator.onLine)
      },
    )
  })
}

// The headless print render takes a boot with no observability/config prelude
// (see bootServerPrintRender); every interactive visit takes the full boot.
async function bootReportPrintRender(
  request: ReportServerRenderRequest,
): Promise<void> {
  // Same egress-free boot as bootServerPrintRender above — the renderer's
  // NetworkPolicy can reach only the in-cluster frontend, so no
  // initConfig/Sentry/replay prelude (see that function's comment).
  try {
    const { default: ReportServerPrintApp } = await import(
      './modules/reports/print/ReportServerPrintApp'
    )
    renderStrict(<ReportServerPrintApp {...request} />)
  } catch (err) {
    await attemptBootRecovery(
      err,
      () => window.location.reload(),
      (e) => Sentry.captureException(e, { extra: { where: 'main.bootReportPrintRender' } }),
    )
  }
}

if (runOfShowServerRenderRequest) {
  void bootServerPrintRender(runOfShowServerRenderRequest)
} else if (reportServerRenderRequest) {
  void bootReportPrintRender(reportServerRenderRequest)
} else {
  void bootApp()
}
